
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Palette, Type, Image, Edit } from 'lucide-react';

const BrandKit = () => {
  // Mock brand colors
  const brandColors = [
    { name: 'Primary', hex: '#FF6B35', variable: '--zuiver-primary' },
    { name: 'Secondary', hex: '#F7C59F', variable: '--zuiver-secondary' },
    { name: 'Tertiary', hex: '#EFEFD0', variable: '--zuiver-light' },
    { name: 'Accent', hex: '#004E89', variable: '--zuiver-accent' },
    { name: 'Dark', hex: '#1A1A1A', variable: '--zuiver-dark' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Brand Guidelines</h3>
          <p className="text-sm text-muted-foreground">Official TORO brand assets and usage guidelines</p>
        </div>
        <Button>
          <Download className="h-4 w-4 mr-2" />
          Download All Assets
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-base">Logo</CardTitle>
                <CardDescription>Official TORO brand logos</CardDescription>
              </div>
              <Button variant="outline" size="icon">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-md p-4 flex flex-col items-center">
                <div className="bg-white p-4 rounded-md mb-2 flex items-center justify-center h-24 w-full">
                  <img 
                    src="/lovable-uploads/f62cf214-d7ba-45a4-9fd0-456ece689b7f.png" 
                    alt="TORO Logo Primary" 
                    className="h-12 object-contain"
                  />
                </div>
                <p className="text-sm">Primary Logo</p>
                <Button variant="ghost" size="small" className="mt-2 w-full">
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
              </div>
              
              <div className="border rounded-md p-4 flex flex-col items-center">
                <div className="bg-black p-4 rounded-md mb-2 flex items-center justify-center h-24 w-full">
                  <img 
                    src="/lovable-uploads/f62cf214-d7ba-45a4-9fd0-456ece689b7f.png" 
                    alt="TORO Logo Inverted" 
                    className="h-12 object-contain"
                  />
                </div>
                <p className="text-sm">Inverted Logo</p>
                <Button variant="ghost" size="small" className="mt-2 w-full">
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-base">Color Palette</CardTitle>
                <CardDescription>Official TORO brand colors</CardDescription>
              </div>
              <Button variant="outline" size="icon">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {brandColors.map((color) => (
                <div key={color.name} className="border rounded-md overflow-hidden">
                  <div 
                    className="h-12" 
                    style={{ backgroundColor: color.hex }}
                  ></div>
                  <div className="p-2">
                    <p className="font-medium text-sm">{color.name}</p>
                    <p className="text-xs text-muted-foreground">{color.hex}</p>
                    <p className="text-xs text-muted-foreground">{color.variable}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-base">Typography</CardTitle>
                <CardDescription>Official TORO brand fonts</CardDescription>
              </div>
              <Button variant="outline" size="icon">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border rounded-md p-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-medium">Inter</p>
                  <Type className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-2xl mb-1">Headline Font</p>
                <p className="text-sm text-muted-foreground">Used for headings, titles, and emphasis</p>
                <Button variant="ghost" size="small" className="mt-2">
                  <Download className="h-3 w-3 mr-1" />
                  Download Font Files
                </Button>
              </div>
              
              <div className="border rounded-md p-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-medium">Inter</p>
                  <Type className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-base mb-1">Body Text Font</p>
                <p className="text-sm text-muted-foreground">Used for body text and general content</p>
                <Button variant="ghost" size="small" className="mt-2">
                  <Download className="h-3 w-3 mr-1" />
                  Download Font Files
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-base">Brand Elements</CardTitle>
                <CardDescription>Additional brand assets</CardDescription>
              </div>
              <Button variant="outline" size="icon">
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-md p-4 flex flex-col items-center">
                <div className="bg-muted p-4 rounded-md mb-2 flex items-center justify-center h-24 w-full">
                  <Palette className="h-12 w-12 text-muted-foreground" />
                </div>
                <p className="text-sm">Icon Library</p>
                <Button variant="ghost" size="small" className="mt-2 w-full">
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
              </div>
              
              <div className="border rounded-md p-4 flex flex-col items-center">
                <div className="bg-muted p-4 rounded-md mb-2 flex items-center justify-center h-24 w-full">
                  <Image className="h-12 w-12 text-muted-foreground" />
                </div>
                <p className="text-sm">Photo Style Guide</p>
                <Button variant="ghost" size="small" className="mt-2 w-full">
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BrandKit;
